import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { setStatusColorMap } from '../lib/utils';

const AuthContext = createContext(null);

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Fire-and-forget: load the configured Job Status colors once after login so
// every Badge across the app can render the custom color.
async function loadStatusColors() {
  try {
    const r = await axios.get(`${API}/job-statuses`);
    setStatusColorMap(r.data || []);
  } catch (e) {
    // Silent — falls back to legacy hardcoded colors.
  }
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('fieldflow_token'));
  const [loading, setLoading] = useState(true);
  const [mustChangePassword, setMustChangePassword] = useState(false);
  // Impersonation: when admin "Tests as User", their original token is stashed in
  // localStorage under fieldflow_admin_token, and we treat fieldflow_token as the
  // active (impersonated) session.
  const [isImpersonating, setIsImpersonating] = useState(!!localStorage.getItem('fieldflow_admin_token'));

  const logout = useCallback(() => {
    localStorage.removeItem('fieldflow_token');
    localStorage.removeItem('fieldflow_admin_token');
    localStorage.removeItem('fieldflow_must_change_password');
    delete axios.defaults.headers.common['Authorization'];
    setToken(null);
    setUser(null);
    setMustChangePassword(false);
    setIsImpersonating(false);
  }, []);

  const fetchUser = useCallback(async (retries = 3) => {
    for (let attempt = 1; attempt <= retries; attempt++) {
      try {
        const response = await axios.get(`${API}/auth/me`, { timeout: 15000 });
        setUser(response.data);
        setLoading(false);
        // Load configured job-status colors once we know the user is authenticated
        loadStatusColors();
        return;
      } catch (error) {
        console.error(`Fetch user attempt ${attempt}/${retries} failed:`, error);
        
        // Only logout on actual auth errors (401), not network errors
        if (error.response?.status === 401) {
          logout();
          setLoading(false);
          return;
        }
        
        // Retry on network errors
        if (attempt < retries) {
          await new Promise(resolve => setTimeout(resolve, 1000 * attempt));
        }
      }
    }
    // After all retries failed, still keep token (don't logout)
    console.log('All fetch user attempts failed, keeping token for manual retry');
    setLoading(false);
  }, [logout]);

  useEffect(() => {
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      fetchUser();
    } else {
      setLoading(false);
    }
  }, [token, fetchUser]);

  const login = async (email, password, retries = 3) => {
    let lastError;
    
    for (let attempt = 1; attempt <= retries; attempt++) {
      try {
        const response = await axios.post(`${API}/auth/login`, { email, password }, {
          timeout: 15000,
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json'
          }
        });
        const { token: newToken, user: userData, must_change_password } = response.data;
        // Clear any stale impersonation state from a previous session that didn't
        // exit cleanly (closing the tab mid-impersonation leaves `fieldflow_admin_token`
        // in localStorage — a fresh login is always the real user, never impersonating).
        localStorage.removeItem('fieldflow_admin_token');
        setIsImpersonating(false);
        localStorage.setItem('fieldflow_token', newToken);
        axios.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;
        setToken(newToken);
        setUser(userData);
        
        // Check if user must change password
        if (must_change_password) {
          localStorage.setItem('fieldflow_must_change_password', 'true');
          setMustChangePassword(true);
        } else {
          localStorage.removeItem('fieldflow_must_change_password');
          setMustChangePassword(false);
        }
        
        return { user: userData, mustChangePassword: must_change_password };
      } catch (err) {
        lastError = err;
        console.log(`Login attempt ${attempt}/${retries} failed:`, err.message);
        
        // If it's a 401 (invalid credentials), don't retry
        if (err.response?.status === 401) {
          throw err;
        }
        
        // Wait before retry (exponential backoff)
        if (attempt < retries) {
          await new Promise(resolve => setTimeout(resolve, 1000 * attempt));
        }
      }
    }
    
    throw lastError;
  };

  const register = async (email, password, fullName, role = 'admin') => {
    const response = await axios.post(`${API}/auth/register`, {
      email,
      password,
      full_name: fullName,
      role
    });
    const { token: newToken, user: userData } = response.data;
    localStorage.setItem('fieldflow_token', newToken);
    axios.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;
    setToken(newToken);
    setUser(userData);
    return userData;
  };

  // Establish a session from a pre-issued JWT (magic link, impersonation, etc.)
  // — matches the side-effects of password login without the network round-trip.
  const loginWithToken = (newToken, userData, mustChangePwd = false) => {
    if (!newToken) return;
    // Magic-link entry should never inherit a stale impersonation flag.
    localStorage.removeItem('fieldflow_admin_token');
    setIsImpersonating(false);
    localStorage.setItem('fieldflow_token', newToken);
    axios.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;
    setToken(newToken);
    if (userData) setUser(userData);
    if (mustChangePwd) {
      localStorage.setItem('fieldflow_must_change_password', 'true');
      setMustChangePassword(true);
    } else {
      localStorage.removeItem('fieldflow_must_change_password');
      setMustChangePassword(false);
    }
  };

  const isAdmin = () => user?.role === 'admin' || user?.role === 'super_user';
  
  const isSuperUser = () => user?.role === 'super_user';

  const hasPermission = (permission) => {
    // Super users have all permissions
    if (isSuperUser()) return true;
    // Admins have most permissions (except office_access which must be explicitly granted)
    if (isAdmin() && permission !== 'office_access') return true;
    // Managers implicitly get manage_certifications without needing the toggle.
    if (permission === 'manage_certifications' && user?.role === 'manager') return true;
    return user?.permissions?.[permission] || false;
  };

  const updatePreference = async (key, value) => {
    try {
      const prefs = { [key]: value };
      await axios.put(`${API}/auth/preferences`, prefs);
      setUser(prev => ({
        ...prev,
        preferences: { ...(prev?.preferences || {}), [key]: value }
      }));
    } catch (e) {
      console.error('Failed to save preference:', e);
    }
  };

  // Begin impersonating a team member (admin-only)
  const impersonate = async (userId) => {
    const adminToken = localStorage.getItem('fieldflow_token');
    if (!adminToken) throw new Error('No active session');
    const response = await axios.post(`${API}/auth/impersonate/${userId}`);
    const newToken = response.data.token;
    // Stash the admin token so we can restore it later
    localStorage.setItem('fieldflow_admin_token', adminToken);
    localStorage.setItem('fieldflow_token', newToken);
    // Hard reload so every page-level fetch uses the impersonated identity from scratch
    window.location.href = '/';
  };

  // Restore the admin's original session
  const stopImpersonating = () => {
    const adminToken = localStorage.getItem('fieldflow_admin_token');
    if (!adminToken) return;
    localStorage.setItem('fieldflow_token', adminToken);
    localStorage.removeItem('fieldflow_admin_token');
    setIsImpersonating(false);
    window.location.href = '/team';
  };

  return (
    <AuthContext.Provider value={{
      user,
      token,
      loading,
      mustChangePassword,
      login,
      register,
      logout,
      isAdmin,
      isSuperUser,
      hasPermission,
      updatePreference,
      refreshUser: fetchUser,
      loginWithToken,
      // Impersonation
      isImpersonating,
      impersonate,
      stopImpersonating
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
